import React, { useCallback, useEffect, useRef } from 'react'
import { CartFooter, CartProducts, CartTableHead, CartTotal, CartTotalContent, CartTotalPrice, CartTotalPriceText, CartTotalPriceValue, CartTotalText, CartTotalValue, CartWrapper } from '../../../../Elements/cart/index.styled'
import Currency from '../../../../Elements/currency'
import { useTranslation } from 'react-i18next'
import { useLocalStorage } from '../../../../../hooks/useLocalStorage'
import Button from '../../../../Elements/buttons/Button'
import { usePrevious } from '../../../../../hooks/usePrevious'
import { useDispatch, useSelector } from 'react-redux'
import CircleLoader from '../../../../Elements/loaders/CircleLoader'
import { getBusinessScopeDiscountThunk } from '../../../../../redux/thunk/cartDiscount'
import { selectCancelItems, selectCancelSubItems, selectCartDiscountValue, selectDiscountOnTable, selectTotalPriceOnTable } from '../../../../../redux/selector/restaurant'
import CartItem from '../../../../Elements/cart/CartItem'
import { useWindowBreakPoint } from '../../../../../hooks/useWindowBreakPoint'
import { theme } from '../../../../../style/theme'
import { removeTableCartCancelItem, removeTableCartCancelSubItem, setTableCancellationDetailsItem } from '../../../../../redux/action/tableCart'
import { checkSavedCartItemsRemoveAbility } from './helper'
import { selectIsTestCompany } from '../../../../../redux/selector/system'

const Cart = ({ showTotal, handleOpenProduct, orders, orderFocusIndex, itemFocusIndex, zIndex, setFocus, incrementQuantity, dropProduct, dropTopping, submitCart, removeRemark, addItemToNewOrder, hasGiftCoupon, handleBackClick, toggleMobileCart }) => {
	const dispatch = useDispatch()
	const [touchPad] = useLocalStorage('touchPad', false)
	const { t } = useTranslation()
	const cartRef = useRef()
	const items = orders ? orders[orders?.length - 1]?.items : null
	const prevItems = usePrevious(items)
	const loading = useSelector(state => state.tableCart.loading)
	const hasNewOrder = orders?.some(order => !order.id)
	const discountValue = useSelector(selectCartDiscountValue(hasGiftCoupon))
	const discountInCurrency = useSelector(selectDiscountOnTable(orders, hasGiftCoupon))
	const totalPrice = useSelector(selectTotalPriceOnTable)
	const enabledDishCourse = useSelector(store => store.restaurantSettings.dishCourses?.enabled)
	const itemsForRemoving = useSelector(selectCancelItems)
	const subItemsForRemoving = useSelector(selectCancelSubItems)
	const disabledBtnSend = loading || (!hasNewOrder && !itemsForRemoving?.length && !subItemsForRemoving?.length)
	const isMobile = useWindowBreakPoint(theme.point720)
	const isTestCompany = useSelector(selectIsTestCompany)

	const savedItemsRemoveAbility = checkSavedCartItemsRemoveAbility(orders, isTestCompany)
	const layoutHead = (touchPad ? 'touchPad' : '') + (enabledDishCourse ? ' dishCourse' : '')

	useEffect(() => {
		dispatch(getBusinessScopeDiscountThunk('RESTAURANT'))
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (items?.length > prevItems?.length) {
			cartRef.current.scrollTop = 100000
		}
	}, [items]) // eslint-disable-line react-hooks/exhaustive-deps

	const openInfoModal = useCallback((event, orderIndex, itemIndex, subItemIndex = null) => {
		event.stopPropagation()
		if (subItemIndex !== null) {
			const subItemPositionId = orders[orderIndex].items[itemIndex].items[subItemIndex].itemPositionId
			const status = orders[orderIndex].items[itemIndex].items[subItemIndex].status
			dispatch(setTableCancellationDetailsItem({ id: subItemPositionId, itemType: 'SUB_ITEM', status }))
		} else {
			const itemPositionId = orders[orderIndex].items[itemIndex].itemPositionId
			const status = orders[orderIndex].items[itemIndex].status
			dispatch(setTableCancellationDetailsItem({ id: itemPositionId, itemType: 'ITEM', status }))
		}
	}, [dispatch, orders])

	const handleBackItemClick = useCallback((event, orderIndex, itemIndex) => {
		event.stopPropagation()
		const itemPositionId = orders[orderIndex].items[itemIndex].itemPositionId
		dispatch(removeTableCartCancelItem(itemPositionId))
	}, [dispatch, orders])

	const handleBackToppingClick = useCallback((event, orderIndex, itemIndex, subItemIndex) => {
		event.stopPropagation()
		const subItemPositionId = orders[orderIndex].items[itemIndex].items[subItemIndex].itemPositionId
		dispatch(removeTableCartCancelSubItem(subItemPositionId))
	}, [dispatch, orders])

	return (
		<CartWrapper className="cart-col">
			<CartTableHead className={layoutHead}>
				{enabledDishCourse && (isMobile ? <div></div> : <div>Gang</div>)}
				<div>ID</div>
				<div>{t('Cart.name')}</div>
				<div>{t('Cart.price')}</div>
				<div>{t('Cart.total')}</div>
			</CartTableHead>
			<CartProducts className={'tableCart ' + (touchPad ? 'touchPad' : '')} ref={cartRef}>
				{orders?.map((order, orderIndex) =>
					order.items?.map((item, itemIndex) =>
						<CartItem key={`${orderIndex}-${itemIndex}`}
											active={orderIndex === orderFocusIndex && itemIndex === itemFocusIndex}
											item={item}
											savedOrder={!!order.id}
											removeAbility={!order.id || savedItemsRemoveAbility}
											onClick={() => setFocus(orderIndex, itemIndex, item)}
											onClickPlus={event => order.id ? addItemToNewOrder(event, item) : incrementQuantity(event, item, itemIndex, orderIndex)}
											onClickMinus={event => dropProduct(event, orderIndex, itemIndex, !order.id, item)}
											onClickToppingMinus={(event, subItemIndex) => dropTopping(event, orderIndex, itemIndex, subItemIndex, !order.id)}
											handleRemoveRemark={(event) => removeRemark(event, orderIndex, itemIndex)}
											enabledDishCourse={enabledDishCourse}
											onClickProduct={(e) => handleOpenProduct(orderIndex, itemIndex, item, e)}
											onClickInfo={event => openInfoModal(event, orderIndex, itemIndex)}
											onClickBack={event => handleBackItemClick(event, orderIndex, itemIndex)}
											onClickBackTopping={(event, subItemIndex) => handleBackToppingClick(event, orderIndex, itemIndex, subItemIndex)}
											onClickToppingInfo={(event, subItemIndex) => openInfoModal(event, orderIndex, itemIndex, subItemIndex)}
											module="RESTAURANT"
						/>))}
			</CartProducts>
			{showTotal &&
				<CartTotal>
					<CartTotalContent className="lf-grid">
						<CartTotalText>{t('Cart.discount')}:</CartTotalText>
						<CartTotalValue>{discountValue}%</CartTotalValue>
						<CartTotalValue>-<Currency>{discountInCurrency}</Currency></CartTotalValue>
					</CartTotalContent>
					<CartTotalPrice>
						<CartTotalPriceText>{t('Cart.totalprice')}</CartTotalPriceText>
						<CartTotalPriceValue className="total">
							<Currency>{totalPrice}</Currency>
						</CartTotalPriceValue>
					</CartTotalPrice>
				</CartTotal>}
			<CartFooter>
				<Button name="back" icon="back" text={t('buttons.controls.back')} keyButton="F4" zIndex={zIndex} onClick={handleBackClick} />
				{isMobile && <Button name="products" icon="products" text={t('restaurant.button_products')} color="blue" keyButton="F4" zIndex={zIndex} onClick={toggleMobileCart} />}
				<Button name="send" icon={!loading && 'checkmark'} text={loading ? t('buttons.controls.apply') : t('buttons.controls.apply')} keyButton="F2" color={loading ? 'gray' : 'green'} zIndex={zIndex} onClick={submitCart} disabled={disabledBtnSend}>
					{loading && <CircleLoader />}
				</Button>
			</CartFooter>
		</CartWrapper>
	)
}

Cart.displayName = 'Cart'
export default Cart
